// URLs of the pre-rendered pages, for linking to them from inside the app.
//
// ⚠️ THESE THREE MUST MATCH `scripts/generate-seo-pages.mjs` BYTE FOR BYTE.
// That script owns the paths - it is what actually writes the files into dist/ -
// and this is a mirror, the same arrangement `townForVenue` in that script has
// with lib/areas.ts. A drift here does not throw, it ships a 404 to Google,
// which is worse than the orphaning this file exists to fix. Change one, change
// both, then `npm run seo` and spot-check a link.
//
// WHY THE APP NEEDS THEM AT ALL. The build writes ~1650 real pages (one per
// event, one per day) with correct titles, descriptions, canonicals and JSON-LD,
// and until now NOTHING in the rendered app linked to any of them: event cards
// and agenda rows were <button onClick> handlers that opened an in-page modal,
// and the only hrefs into the corpus lived in index.html's <noscript> block.
// Googlebot executes JavaScript, so it renders the app and never reads that
// noscript - which left every one of those pages reachable only by sitemap, from
// a domain registered 2026-07-20, with no internal link equity flowing to them.
// `site:calendar.lyhevents.com` showed the signature: the homepage, a handful of
// expired event pages, and not one day page.

/** Same rules as slugify() in scripts/generate-seo-pages.mjs. */
import { hostedByName } from './hostedBy'

export function slugify(s: string): string {
  return (
    String(s)
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'event'
  )
}

/**
 * The event's own page. The id suffix is what makes it unique rather than
 * decoration: two venues both run a "Trivia Night" and the name alone collides.
 *
 * This is the path for a one-off. Anything that runs on more than one date
 * shares a single page instead, and `buildEventPaths` below is what decides
 * which events those are.
 */
export function soloEventPath(event: { id: string; name: string }): string {
  return `/e/${slugify(event.name)}-${event.id.slice(0, 8)}/`
}

/**
 * Every path, keyed by event id, once the app knows the full set.
 *
 * Module-level and mutable on purpose: it mirrors the `eventPaths` map in
 * scripts/generate-seo-pages.mjs exactly, and the property that matters most
 * about these two is that they behave the same. Threading a map down through
 * EventCard and AgendaRow would buy purity and cost the symmetry.
 *
 * Empty until registerEventPaths runs, and eventPath falls back to the one-off
 * path until then, so a link rendered early points at a url that 301s to the
 * right page rather than at nothing.
 */
let sharedPaths = new Map<string, string>()

/** Call once whenever the loaded event set changes. */
export function registerEventPaths(events: readonly PathEvent[]): void {
  sharedPaths = stableEventPaths(events)
}

export function eventPath(event: { id: string; name: string }): string {
  return sharedPaths.get(event.id) ?? soloEventPath(event)
}

/**
 * What makes two rows the same event: the same name at the same venue.
 *
 * Slugged on both halves rather than compared raw, so fixing a comma in a venue
 * string does not silently split a group in two and change a live URL.
 */
export function eventGroupKey(event: { name: string; venue?: string | null }): string {
  return `${slugify(event.name)}@${slugify(canonicalVenue(event.venue))}`
}

/**
 * The venue half of the key, canonicalised the way the venue PAGES already do it.
 *
 * Keying on the raw string looked right and quietly leaked. The same place gets
 * typed several ways across rows - "Johnson Farm, Peaks of Otter, Bedford" and
 * "Johnson Farm, Bedford", four spellings of the Miller Center, "Hammer & Stain"
 * with and without ", Lynchburg" - and each spelling became its own page, which
 * is the duplication this whole thing exists to remove. hostedByName already
 * resolves all of those to one name, which is how 82 venue pages group; the
 * event pages just were not asking it.
 *
 * Falls back to the raw string when hostedByName has no answer, so a venue it
 * has never seen still groups with itself rather than not at all.
 */
function canonicalVenue(venue?: string | null): string {
  if (!venue) return ''
  return hostedByName(venue) ?? venue
}

/** FNV-1a, 32 bit, as 8 hex characters. Small, stable, and dependency free. */
function hash8(input: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(16).padStart(8, '0')
}

/**
 * One path per event, with every date of a repeating event pointing at one page.
 *
 * WHY. `lyh_events` stores one row per SESSION, so a four-day fair is four rows
 * and used to be four pages, each ranking against the other three. Search
 * Console for Aug 6 to Sep 4 measured it: 44 slugs had more than one url, and
 * between them they took 643 of 1,677 impressions and returned 12 clicks. The
 * Amherst County Fair alone was four urls, 196 impressions and no clicks, while
 * the queries behind them asked for "schedule", "tickets" and "when is" - which
 * is a question a single date's page cannot answer and a page listing all four
 * days can.
 *
 * The group's path is keyed on a hash of the group, NOT on any one member's id.
 * A member id would look stable and would not be: sessions age out of the
 * window every week, so the "first" row keeps changing and the canonical url
 * would churn with it.
 *
 * One-offs keep the id-suffixed path they already have. Rewriting ~900 urls
 * that rank perfectly well, to gain nothing but a tidier rule, is how you spend
 * ranking you already earned.
 *
 * Since 2026-09-23 this is no longer what decides a url on its own: it runs
 * over the frozen set inside stableEventPaths below, and alone only for the
 * generator's redirect bookkeeping. Solo-or-shared by count inside a window is
 * what made urls move when a date was added; see stableEventPaths for why.
 */
/**
 * The grouping as it shipped on 2026-09-06, keyed on the RAW venue string.
 *
 * Kept only so the generator can redirect the urls that keying produced. It was
 * live for about an hour before the hostedByName fix; short enough that Google
 * almost certainly never indexed one, long enough that leaving them to the SPA
 * catch-all would be sloppy.
 */
export function legacyEventGroupKey(event: { name: string; venue?: string | null }): string {
  return `${slugify(event.name)}@${slugify(event.venue ?? '')}`
}

export function buildEventPaths(
  events: readonly { id: string; name: string; venue?: string | null }[],
  keyOf: (e: { name: string; venue?: string | null }) => string = eventGroupKey,
): Map<string, string> {
  const groups = new Map<string, { id: string; name: string }[]>()
  for (const e of events) {
    const key = keyOf(e)
    const list = groups.get(key)
    if (list) list.push(e)
    else groups.set(key, [e])
  }

  const paths = new Map<string, string>()
  for (const [key, members] of groups) {
    if (members.length === 1) {
      paths.set(members[0].id, soloEventPath(members[0]))
      continue
    }
    const shared = `/e/${slugify(members[0].name)}-${hash8(key)}/`
    for (const m of members) paths.set(m.id, shared)
  }
  return paths
}

/** What stableEventPaths needs from a row. Both callers pass full event rows. */
export type PathEvent = {
  id: string
  name: string
  venue?: string | null
  start_time: string
  created_at?: string | null
}

// The url freeze of 2026-09-23. Every url that existed on that day was produced
// by buildEventPaths above, run over the app window the generator used that
// morning: approved rows starting (Eastern) from FROZEN_FROM to FROZEN_TO. Those
// rows, and only those, keep being grouped exactly that way forever.
//
// WHY A FREEZE AND NOT A NEW RULE FOR EVERYONE. buildEventPaths decides solo or
// shared from how many rows a group has INSIDE A SLIDING WINDOW, so a url moved
// whenever that count crossed one: a one-off became a hash url the day a second
// date was added, and a hash group became a solo url the week all but one of its
// dates aged out. The Amherst County Fair went through five urls that way; four
// of them 301 to /e/amherst-county-fair-7d2fe45e/, which inherited none of their
// ranking. Search Console on 2026-09-23 had 437 pages in "Discovered, currently
// not indexed", 246 of them upcoming events, and a url that keeps moving is one
// Google keeps rediscovering instead of crawling. Any rule that re-derives the
// whole set would re-shuffle the ~2,500 urls that are live and ranking, which is
// the same damage again, so the live set is pinned by construction instead and
// the new rule only ever applies to rows the old one never placed.
//
// FROZEN_CREATED is the instant the baseline for this change was built. A row
// created after it was never placed by the old rule, whatever its dates. It has
// to predate the first deploy of this code; if a scheduled build runs the OLD
// code first, rows created between the two builds get old-rule urls that this
// rule may move, so ship before the next cron or bump all three together and
// re-diff the dist/e/ listing against a fresh baseline.
const FROZEN_FROM = '2026-09-09'
const FROZEN_TO = '2026-12-22'
const FROZEN_CREATED = Date.parse('2026-09-23T14:00:00Z')

const EASTERN_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** YYYY-MM-DD in Eastern time; the generator's window is Eastern calendar days. */
function easternDate(iso: string): string {
  const p: Record<string, string> = {}
  for (const part of EASTERN_DATE.formatToParts(new Date(iso))) p[part.type] = part.value
  return `${p.year}-${p.month}-${p.day}`
}

/** Created before the freeze. A row with no created_at is treated as old. */
function predatesFreeze(e: PathEvent): boolean {
  const t = e.created_at ? Date.parse(e.created_at) : NaN
  return !(t > FROZEN_CREATED)
}

/**
 * One path per event, where a url, once served, does not move when a date is
 * added to or removed from its event.
 *
 * Three kinds of row, decided per row from its own dates and nothing else, so
 * the answer does not depend on which window the caller loaded (the generator
 * loads every approved row, the app does too):
 *
 *   FROZEN   created before the freeze and starting inside the frozen window.
 *            Grouped among themselves by buildEventPaths, which reproduces the
 *            2026-09-23 urls exactly. Adding rows cannot change this grouping
 *            because new rows are never members of it.
 *   ARCHIVE  created before the freeze and starting before the window. These
 *            were outside every grouping the generator ran, so their pages sit
 *            at the solo path, and they stay there.
 *   NEW      everything else: created after the freeze, or created before it
 *            but starting after the window (not yet paged on 2026-09-23).
 *            A NEW row joins its group's frozen url when that group has one, so
 *            a live one-off keeps its url when a second date is added. Otherwise
 *            the group takes the solo path of its ANCHOR, the earliest-created
 *            NEW row, so a one-off published after the freeze already sits at
 *            the url it will keep when it grows.
 *
 * Tie-break and removal. The anchor is the lowest created_at, then the lowest
 * id, so two rows inserted in one statement (same created_at) always pick the
 * same one. If the anchor is later rejected or deleted, the group moves ONCE to
 * the next-earliest row's solo path, and it cannot swing back: the rows left
 * never get older, and a new row always sorts after them. The same is true of a
 * frozen group that loses a row to a rejection: it can drop from a hash url to
 * the survivor's solo url, once. Both are the price of computing this from the
 * rows instead of storing it, and both are rare, since approved rows are almost
 * never un-approved.
 */
export function stableEventPaths(events: readonly PathEvent[]): Map<string, string> {
  const frozen: PathEvent[] = []
  const fresh: PathEvent[] = []
  const paths = new Map<string, string>()
  for (const e of events) {
    if (!predatesFreeze(e)) {
      fresh.push(e)
      continue
    }
    const date = easternDate(e.start_time)
    if (date > FROZEN_TO) fresh.push(e)
    else if (date >= FROZEN_FROM) frozen.push(e)
    else paths.set(e.id, soloEventPath(e))
  }

  const frozenPaths = buildEventPaths(frozen)
  const frozenByKey = new Map<string, string>()
  for (const e of frozen) {
    const path = frozenPaths.get(e.id)!
    paths.set(e.id, path)
    frozenByKey.set(eventGroupKey(e), path)
  }

  const anchors = new Map<string, PathEvent>()
  for (const e of fresh) {
    const key = eventGroupKey(e)
    if (frozenByKey.has(key)) continue
    const cur = anchors.get(key)
    if (!cur || earlierCreated(e, cur)) anchors.set(key, e)
  }
  for (const e of fresh) {
    const key = eventGroupKey(e)
    paths.set(e.id, frozenByKey.get(key) ?? soloEventPath(anchors.get(key)!))
  }
  return paths
}

function earlierCreated(a: PathEvent, b: PathEvent): boolean {
  const ta = a.created_at ? Date.parse(a.created_at) : 0
  const tb = b.created_at ? Date.parse(b.created_at) : 0
  return ta !== tb ? ta < tb : a.id < b.id
}

/** The day page for an Eastern calendar date, `YYYY-MM-DD`. */
export function dayPath(date: string): string {
  return `/day/${date}/`
}

/**
 * True when a click should be left alone so the browser can follow the href.
 *
 * Cards open a modal on a plain click, which is the whole point of the app. But
 * a cmd/ctrl-click, a shift-click, a middle-click or a right-click all mean "I
 * want this somewhere else", and swallowing those was never deliberate - it was
 * a side effect of the card being a <button>. Now that there is a real href
 * behind it, those gestures open the static page, which is a better answer than
 * nothing happening.
 */
export function wantsNewContext(e: React.MouseEvent): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0
}

/**
 * Venue pages, and which venues actually have one.
 *
 * The generator only writes a venue page once a venue has VENUE_MIN_EVENTS (3)
 * UPCOMING events - a few hundred pages each carrying a single listing is thin
 * content at scale. So the app cannot link every venue: it has to know which
 * ones were built, or a link lands on the SPA catch-all and reads as a 404 that
 * returns 200.
 *
 * Same threshold and the same hostedByName grouping as
 * scripts/generate-seo-pages.mjs. If that constant moves, this moves with it.
 */
const VENUE_MIN_EVENTS = 3

let venuesWithPages = new Set<string>()

export function registerVenuePaths(
  events: readonly { start_time: string; venue?: string | null }[],
): void {
  const today = new Date().toISOString().slice(0, 10)
  const counts = new Map<string, number>()
  for (const e of events) {
    if (e.start_time.slice(0, 10) < today) continue
    const name = hostedByName(e.venue ?? null)
    if (!name) continue
    counts.set(name, (counts.get(name) ?? 0) + 1)
  }
  venuesWithPages = new Set(
    [...counts.entries()].filter(([, n]) => n >= VENUE_MIN_EVENTS).map(([name]) => name),
  )
}

/** The venue's page, or null when it does not have one. */
export function venuePathFor(venue: string | null | undefined): string | null {
  const name = hostedByName(venue ?? null)
  if (!name || !venuesWithPages.has(name)) return null
  return `/venue/${slugify(name)}/`
}
